import React, { Component } from "react";
import Joi from "joi-browser";
import Input from "./input";
import Select from "./select";
import CheckBox from "./checkbox";
import { TextArea, CommentBox } from "./textarea";
import CurrencyInput from "./currencyInput";
import SearchableSelect from "./searchableSelect";
import LoginFloatingInput from "./loginFloatingInput";

class Form extends Component {
  state = {
    data: {},
    errors: {}
  };

  validate = () => {
    const options = { abortEarly: false };
    const { error } = Joi.validate(this.state.data, this.schema, options);
    if (!error) return null;

    const errors = {};
    for (let item of error.details) errors[item.path[0]] = item.message;
    return errors;
  };

  validateProperty = ({ name, value }) => {
    const obj = { [name]: value };
    const schema = { [name]: this.schema[name] };
    const { error } = Joi.validate(obj, schema);
    return error ? error.details[0].message : null;
  };

  handleSubmit = e => {
    e.preventDefault();

    const errors = this.validate();
    this.setState({ errors: errors || {} });
    if (errors) return;

    this.doSubmit();
  };

  handleChange = ({ currentTarget: input }) => {
    const errors = { ...this.state.errors };
    const errorMessage = this.validateProperty(input);
    if (errorMessage) errors[input.name] = errorMessage;
    else delete errors[input.name];

    const data = { ...this.state.data };
    data[input.name] = input.value;

    this.setState({ data, errors });
  };

  handleCurrencyChange = ({ currentTarget: input }) => {
    const errors = { ...this.state.errors };
    const errorMessage = this.validateProperty(input);
    if (errorMessage) errors[input.name] = errorMessage;
    else delete errors[input.name];
  
    const data = { ...this.state.data };
    // Remove non-digit characters from the input value
    const inputValue = input.value.replace(/[^0-9]/g, '');
    // Format the input value as currency (Naira in this case)
    // const formattedValue = Number(inputValue).toLocaleString('en-NG', {
    //   style: 'currency',
    //   currency: 'NGN',
    // });

    const formattedValue = Intl.NumberFormat("en-NG", { 
      style: "currency", 
      currency: "NGN" 
    }).format(inputValue);

    // Update the displayed value in the input field
    input.value = formattedValue;
  
    // Store the input value as an integer in the data state
    data[input.name] = parseInt(inputValue, 10); // or parseFloat for decimal values
  
    this.setState({ data, errors });
  };
  

  handleCommentBoxChange = ({ currentTarget: input }) => {
    let comment = {...this.state.comment};
    comment[input.name] = input.value;
    this.setState({ comment });
  };

  renderButton(label, icon, block = "", color = "primary") {
    let className=(block === "") ? `btn btn-${color} btn-sm` : `btn btn-${color} btn-block mt-5`
    return (
      <button 
        disabled={this.validate()} 
        className={className}
      >
        {icon} {label}
      </button>
    );
  }

  renderBackButton(backTo) {
    const derivedBackTo = (() => {
      const pathname = this.props.location?.pathname;
      if (!pathname) return null;
      const firstSegment = pathname.split("/").filter(Boolean)[0];
      return firstSegment ? `/${firstSegment}` : null;
    })();

    const target = backTo || this.props.location?.state?.backTo || derivedBackTo;

    return (
      <button
        type="button"
        className="btn btn-light btn-sm"
        onClick={() => {
          if (target && this.props.history && this.props.history.push) {
            this.props.history.push(target);
            return;
          }
          if (this.props.history && this.props.history.goBack) {
            this.props.history.goBack();
            return;
          }
          window.history.back();
        }}
      >
        <i className="fa fa-arrow-left" /> <span className="d-none d-sm-inline">Back</span>
      </button>
    );
  }

  renderSelect(name, label, options, disabled = false) {
    const { data, errors } = this.state;

    return (
      <Select
        name={name}
        value={data[name]}
        label={label}
        options={options}
        disabled={disabled}
        onChange={this.handleChange}
        error={errors[name]}
      />
    );
  }

  renderFloatingInput(name, label, type = "text", autoComplete = "") {
    const { data, errors } = this.state;

    return (
      <LoginFloatingInput
        id={name}
        name={name}
        type={type}
        label={label}
        value={data[name]}
        onChange={this.handleChange}
        autoComplete={autoComplete}
        error={errors[name]}
      />
    );
  }

  renderSearchableSelect(name, label, options, disabled = false, extraProps = {}) {
    const { data, errors } = this.state;

    return (
      <SearchableSelect
        name={name}
        value={data[name]}
        label={label}
        options={options}
        disabled={disabled}
        onChange={this.handleChange}
        error={errors[name]}
        {...extraProps}
      />
    );
  }  

  renderInput(name, label, type = "text", readOnly = false, currency = false ) {
    const { data, errors } = this.state;

    return (
      <Input
        type={type}
        name={name}
        value={data[name]}
        label={label}
        readOnly={readOnly}
        onChange={currency ? this.handleCurrencyChange : this.handleChange}
        error={errors[name]}
      />
    );
  }

  renderCurrencyInput(name, label, disabled = false, comment = "") {
    const { data, errors } = this.state;

    return (
      <CurrencyInput
        name={name}
        value={data[name]}
        label={label}
        onChange={this.handleChange}
        error={errors[name]}
        disabled={disabled}
        comment={comment}
      />
    );
  }

  renderCheckBox(name, label) {
    const { data, errors } = this.state;

    return (
      <CheckBox
        name={name}
        value={data[name]}
        label={label}
        checked={data[name]}
        onChange={this.handleChange}
        error={errors[name]}
      />
    );
  }

  renderTextArea(name, label, options) {
    const { data, errors } = this.state;

    return (
      <TextArea
        name={name}
        value={data[name]}
        label={label}
        options={options}
        onChange={this.handleChange}
        error={errors[name]}
      />
    );
  }

  renderCommentBox(name, label, options) {
    // const { data, errors } = this.state;

    return (
      <CommentBox
        name={name}
        value={this.state.comment.comment}
        label={label}
        options={options}
        onChange={this.handleCommentBoxChange}
      />
    );
  }

  renderFormButtonBar(button = true, options = {}) {
    const { showBack = true, rightButtons = null, backTo = null } = options;

    return (
      <div className="p-1 shadow-sm bg-light">
        <span className="float-right">
          {!!button && this.renderButton("Save", <i className="fa fa-save" />, "", "light")}
          {rightButtons}
        </span>
        <span>
          {!!showBack && this.renderBackButton(backTo)}
        </span>
      </div>
    );
  }

  renderFormTitleRow(options = {}) {
    const { titleRight = null } = options;

    return (
      <div className="d-flex justify-content-between align-items-center pt-2 pb-3">
        <h4 className="text-secondary m-0">{this.state.title}</h4>
        {titleRight}
      </div>
    );
  }

  renderFormHeading(button = true, options = {}) {
    // const { data, errors } = this.state;

    const { showBack = true, rightButtons = null, titleRight = null, backTo = null } = options;

    return (
      <React.Fragment>
        {this.renderFormButtonBar(button, { showBack, rightButtons, backTo })}
        {this.renderFormTitleRow({ titleRight })}
      </React.Fragment>
      );
  }
}

export default Form;
